import { useCallback, useEffect, useState } from 'react';
import { useAnalyticsSelectionStore } from '../../../../store/analyticsSelectionStore';
import { IAnalytic, PREPROCESSED_ANALYTICS } from '../../../../types/analytics';
import { ICurrentUser } from '../../../../types/user';
import { UserGroupPermissions } from '../../../../types/userGroupPermissions';
import { EVENT_ANALYTICS_TYPE_SELECTED, trackUpdate } from '../../../../utils/matomoEvents';

const basicAnalytics = ['Time Series', 'Rose Plot', 'Scatter Plot'];
const firstPreviewAnalytics = ['Time Series'];

export const useAnalyticsNavigation = (
  markerId: string,
  analyticsDefinition: string,
  userGroupPermissions: UserGroupPermissions,
  user: ICurrentUser,
  isTnw: boolean,
  isAuthenticated: () => boolean,
  coordinates: number[],
  previewDatasetId: string,
  setShowLogin: (show: boolean) => void,
  isAnalytics: boolean,
  cancelAllOperation: () => void
) => {
  const [availableAnalytics, setAvailableAnalytics] = useState<any[]>([]);
  const activePlotAnalytics = useAnalyticsSelectionStore(
    (state) => state.getMarkerSelection(markerId).activePlotAnalytics
  );
  const setActivePlotAnalytics = useAnalyticsSelectionStore(
    (state) => state.setActivePlotAnalytics
  );
  const isAdvanceAnalytics = useAnalyticsSelectionStore(
    (state) => state.getMarkerSelection(markerId).isAdvanceAnalytics
  );
  const setIsAdvanceAnalytics = useAnalyticsSelectionStore((state) => state.setIsAdvanceAnalytics);

  useEffect(() => {
    if (!isAnalytics) return;
    if (activePlotAnalytics.length === 0) {
      setActivePlotAnalytics(markerId, firstPreviewAnalytics);
    }
  }, [isAnalytics, markerId, activePlotAnalytics.length, setActivePlotAnalytics]);

  useEffect(() => {
    if (!isAnalytics) return;
    if (analyticsDefinition) {
      const analytcsDefs = analyticsDefinition.split('|');

      const analytics = analytcsDefs
        .filter((a) => a !== Object.keys(PREPROCESSED_ANALYTICS)[0])
        .map((analyticDef: string) => {
          const isDisabled =
            basicAnalytics.includes(analyticDef) || isTnw
              ? false
              : userGroupPermissions?.analyticsList.toString().toLowerCase() === 'limited';

          return {
            id: analyticDef,
            name: analyticDef,
            basic: basicAnalytics.includes(analyticDef),
            disabled: isDisabled,
          };
        });

      const findActiveAnalytics = analytics.find((item) => item.id === activePlotAnalytics[0]);
      if (!findActiveAnalytics) {
        setActivePlotAnalytics(markerId, firstPreviewAnalytics);
      }

      setAvailableAnalytics(analytics);
    }
  }, [
    analyticsDefinition,
    user,
    userGroupPermissions,
    isTnw,
    isAnalytics,
    markerId,
    activePlotAnalytics,
    setActivePlotAnalytics,
  ]);

  const handleAnalyticsTypeChanged = useCallback(
    (analytic: IAnalytic) => {
      if (!isAuthenticated() && !basicAnalytics.includes(analytic.id)) {
        setShowLogin(true);
        return;
      }
      if (analytic.disabled === false) {
        cancelAllOperation();
        if (!basicAnalytics.includes(analytic.id)) {
          setIsAdvanceAnalytics(markerId, true);
        }
        setActivePlotAnalytics(markerId, [analytic.id]);
        trackUpdate(
          EVENT_ANALYTICS_TYPE_SELECTED,
          coordinates,
          previewDatasetId,
          analytic.id.toLowerCase()
        );
      }
    },
    [
      coordinates,
      previewDatasetId,
      isAuthenticated,
      setShowLogin,
      markerId,
      setActivePlotAnalytics,
      setIsAdvanceAnalytics,
    ]
  );

  return {
    isAdvanceAnalytics,
    availableAnalytics,
    activePlotAnalytics,
    handleAnalyticsTypeChanged,
  };
};
